import type {
  WebsiteComponentId,
  WebsiteLayoutFamily,
  WebsitePageKind,
} from '../../types/website-templates';

export const WEBSITE_RENDERER_MAJOR = 1 as const;

/**
 * A7 capability registry: the variants each layout family's code can render.
 * MUST equal the SQL `website_renderer_capabilities` seed (parity test). A
 * manifest that names anything else is rejected before it can be published.
 */
export const WEBSITE_COMPONENT_VARIANTS: Readonly<
  Record<WebsiteLayoutFamily, Readonly<Partial<Record<WebsiteComponentId, readonly string[]>>>>
> = {
  atelier: {
    'chrome.announcement': ['atelier-bar'],
    'chrome.header': ['atelier-centered'],
    'chrome.footer': ['atelier-footer'],
    'home.hero': ['atelier-carousel'],
    'home.categoryTiles': ['atelier-circles'],
    'home.productRail': ['atelier-rail'],
    'home.productGrid': ['atelier-grid'],
    'home.promoBanner': ['atelier-editorial'],
    'home.featureStrip': ['atelier-strip'],
    'home.aboutTeaser': ['atelier-teaser'],
    'catalog.listing': ['atelier-grid'],
    'product.detail': ['atelier-gallery'],
    'product.relatedRail': ['atelier-rail'],
    'cart.page': ['atelier-cart'],
    'checkout.page': ['atelier-checkout'],
    'order.confirmation': ['atelier-confirmation'],
    'storeInfo.page': ['atelier-info'],
  },
  stride: {
    'chrome.announcement': ['stride-bar'],
    'chrome.header': ['stride-compact'],
    'chrome.footer': ['stride-footer'],
    'home.hero': ['stride-fullbleed'],
    'home.categoryTiles': ['stride-squares'],
    'home.productRail': ['stride-rail'],
    'home.productGrid': ['stride-grid'],
    'home.promoBanner': ['stride-split'],
    'home.aboutTeaser': ['stride-teaser'],
    'catalog.listing': ['stride-grid'],
    'product.detail': ['stride-gallery'],
    'product.relatedRail': ['stride-rail'],
    'cart.page': ['stride-cart'],
    'checkout.page': ['stride-checkout'],
    'order.confirmation': ['stride-confirmation'],
    'storeInfo.page': ['stride-info'],
  },
  'editorial-fashion': {
    'chrome.announcement': ['fashion-bar'],
    'chrome.header': ['fashion-header'],
    'chrome.footer': ['fashion-footer'],
    'home.hero': ['fashion-hero'],
    'home.categoryTiles': ['fashion-portraits'],
    'home.categoryCampaign': ['fashion-campaign'],
    'home.aboutTeaser': ['fashion-editorial'],
    'home.newsletter': ['fashion-email'],
    'catalog.listing': ['fashion-grid'],
    'product.detail': ['fashion-gallery'],
    'product.relatedRail': ['fashion-related'],
    'cart.page': ['fashion-cart'],
    'checkout.page': ['fashion-checkout'],
    'order.confirmation': ['fashion-confirmation'],
    'storeInfo.page': ['fashion-info'],
  },
  beauty: {
    'chrome.announcement': ['beauty-bar'],
    'chrome.header': ['beauty-centered'],
    'chrome.newsletter': ['beauty-newsletter'],
    'chrome.footer': ['beauty-footer'],
    'home.hero': ['beauty-editorial'],
    'home.promoBanner': ['beauty-campaign'],
    'home.categoryTiles': ['beauty-tiles'],
    'home.productRail': ['beauty-rail'],
    'home.mission': ['beauty-mission'],
    'home.community': ['beauty-community'],
    'catalog.listing': ['beauty-grid'],
    'product.detail': ['beauty-gallery'],
    'cart.page': ['beauty-cart'],
    'checkout.page': ['beauty-checkout'],
    'order.confirmation': ['beauty-confirmation'],
    'storeInfo.page': ['beauty-info'],
  },
  'luxury-jewelry': {
    'chrome.announcement': ['luxury-bar'],
    'chrome.header': ['luxury-centered'],
    'chrome.footer': ['luxury-footer'],
    'home.hero': ['luxury-editorial'],
    'home.featureStrip': ['luxury-benefits'],
    'home.categoryTiles': ['luxury-tiles'],
    'home.productGrid': ['luxury-showcase'],
    'home.promoBanner': ['luxury-story-media-first', 'luxury-story-text-first', 'luxury-campaign'],
    'catalog.listing': ['luxury-grid'],
    'product.detail': ['luxury-gallery'],
    'cart.page': ['luxury-cart'],
    'checkout.page': ['luxury-checkout'],
    'order.confirmation': ['luxury-confirmation'],
    'storeInfo.page': ['luxury-info'],
  },
  'legacy-engine-v2': {
    'legacy.enginePage': ['engine-v1', 'engine-v2'],
    'catalog.listing': ['legacy-default'],
    'product.detail': ['legacy-default'],
    'cart.page': ['legacy-default'],
    'checkout.page': ['legacy-default'],
    'order.confirmation': ['legacy-default'],
    'storeInfo.page': ['legacy-default'],
  },
};

export interface WebsiteRendererCapabilityRow {
  readonly componentId: WebsiteComponentId;
  readonly variant: string;
  readonly layoutFamily: WebsiteLayoutFamily;
  readonly rendererMajor: typeof WEBSITE_RENDERER_MAJOR;
}

/** Flat rows in stable order — the exact SQL seed. */
export function listWebsiteRendererCapabilities(): readonly WebsiteRendererCapabilityRow[] {
  const rows: WebsiteRendererCapabilityRow[] = [];
  for (const layoutFamily of Object.keys(WEBSITE_COMPONENT_VARIANTS) as WebsiteLayoutFamily[]) {
    const components = WEBSITE_COMPONENT_VARIANTS[layoutFamily];
    for (const [componentId, variants] of Object.entries(components) as [
      WebsiteComponentId,
      readonly string[],
    ][]) {
      for (const variant of variants) {
        rows.push({ componentId, variant, layoutFamily, rendererMajor: WEBSITE_RENDERER_MAJOR });
      }
    }
  }
  return rows;
}

export function isSupportedWebsiteVariant(
  layoutFamily: WebsiteLayoutFamily,
  component: WebsiteComponentId,
  variant: string,
): boolean {
  return WEBSITE_COMPONENT_VARIANTS[layoutFamily][component]?.includes(variant) ?? false;
}

/** Which surface a component may appear on. `chrome` is the site frame. */
export const WEBSITE_COMPONENT_SURFACE: Readonly<
  Record<WebsiteComponentId, WebsitePageKind | 'chrome'>
> = {
  'chrome.announcement': 'chrome',
  'chrome.header': 'chrome',
  'chrome.footer': 'chrome',
  'chrome.newsletter': 'chrome',
  'home.hero': 'home',
  'home.categoryTiles': 'home',
  'home.productRail': 'home',
  'home.productGrid': 'home',
  'home.promoBanner': 'home',
  'home.featureStrip': 'home',
  'home.aboutTeaser': 'home',
  'home.mission': 'home',
  'home.community': 'home',
  'home.categoryCampaign': 'home',
  'home.newsletter': 'home',
  'catalog.listing': 'catalog',
  'product.detail': 'product',
  'product.relatedRail': 'product',
  'cart.page': 'cart',
  'checkout.page': 'checkout',
  'order.confirmation': 'order',
  'storeInfo.page': 'store-info',
  'legacy.enginePage': 'home',
};

/**
 * A8: the platform commerce component every page must carry. Templates pick
 * the variant; they cannot drop the component.
 */
export const WEBSITE_REQUIRED_PAGE_COMPONENT: Readonly<
  Partial<Record<WebsitePageKind, WebsiteComponentId>>
> = {
  catalog: 'catalog.listing',
  product: 'product.detail',
  cart: 'cart.page',
  checkout: 'checkout.page',
  order: 'order.confirmation',
  'store-info': 'storeInfo.page',
};

export const WEBSITE_REQUIRED_CHROME_COMPONENTS = [
  'chrome.header',
  'chrome.footer',
] as const satisfies readonly WebsiteComponentId[];
